import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faSearch } from '@fortawesome/free-solid-svg-icons';
import { searchContent } from '../../api';
import ContentCard from '../Cards/ContentCard';
import HoverCard from '../Cards/HoverCard';

const PageContainer = styled.div`
  padding: 100px 30px 20px; /* Added top padding for header */
  
  @media (max-width: 768px) {
    padding: 100px 20px 20px; /* Added top padding for header */
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
    color: var(--primary-light);
  }
`;

const ResultsSection = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 15px;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
  min-height: 200px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
`;

const NoResults = styled.div`
  color: var(--text-dim);
  padding: 40px 20px;
  text-align: center;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin: 20px 0;
  
  h3 {
    font-size: 20px;
    margin-bottom: 10px;
    color: var(--text-light);
  }
  
  p {
    max-width: 500px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

// Loading overlay
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  svg {
    font-size: 40px;
    color: var(--primary-light);
    animation: spin 1s linear infinite;
  }
  
  p {
    margin-top: 15px;
    color: white;
    font-size: 16px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inline loading indicator for grid
const GridLoadingIndicator = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px 0;
  
  svg {
    font-size: 30px;
    color: var(--primary-light);
    animation: spin 1s linear infinite;
  }
`;

const SearchPage = () => {
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ movies: [], shows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get('q');
    
    if (searchQuery) {
      setQuery(searchQuery);
      performSearch(searchQuery);
    }
  }, [location.search]);
  
  const performSearch = async (searchQuery) => {
    try {
      setLoading(true);
      const searchResults = await searchContent(searchQuery);
      console.log('Search results:', searchResults);
      
      // Normalize data format for movies and shows
      const normalizedResults = {
        movies: searchResults.movies.map(movie => ({
          ...movie,
          type: 'movie',
          id: movie._id || movie.id // Ensure id is available in both formats
        })),
        shows: searchResults.shows.map(show => ({
          ...show,
          type: 'show',
          id: show._id || show.id // Ensure id is available in both formats
        }))
      };
      
      setResults(normalizedResults);
    } catch (err) {
      console.error('Error searching content:', err);
      setError('Failed to search content. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <LoadingOverlay>
        <LoadingSpinner>
          <FontAwesomeIcon icon={faSpinner} />
          <p>Searching for "{query}"...</p>
        </LoadingSpinner>
      </LoadingOverlay>
    );
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  const { movies, shows } = results;
  const hasResults = movies.length > 0 || shows.length > 0;
  
  return (
    <PageContainer>
      <PageTitle>
        <FontAwesomeIcon icon={faSearch} />
        Search Results for "{query}"
      </PageTitle>
      
      {!hasResults && (
        <NoResults>
          <h3>No results found</h3>
          <p>
            We couldn't find any content matching your search. 
            Try different keywords or check for spelling errors.
          </p>
        </NoResults>
      )}
      
      {movies.length > 0 && (
        <ResultsSection>
          <SectionTitle>Movies</SectionTitle>
          <ResultsGrid>
            {movies.map(movie => (
              movie.hoverImage ? (
                <HoverCard key={movie.id || movie._id} item={movie} type="movie" />
              ) : (
                <ContentCard key={movie.id || movie._id} item={movie} type="movie" />
              )
            ))}
          </ResultsGrid>
        </ResultsSection>
      )}
      
      {shows.length > 0 && (
        <ResultsSection>
          <SectionTitle>TV Shows</SectionTitle>
          <ResultsGrid>
            {shows.map(show => (
              show.hoverImage ? (
                <HoverCard key={show.id || show._id} item={show} type="show" />
              ) : (
                <ContentCard key={show.id || show._id} item={show} type="show" />
              )
            ))}
          </ResultsGrid>
        </ResultsSection>
      )}
    </PageContainer>
  );
};

export default SearchPage;